/**
 * Auth Callback - Handles Supabase auth redirects
 *
 * This page handles:
 * - Email verification redirects
 * - Password reset redirects
 * - OAuth callbacks (if added later)
 */

import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { supabase } from '../../lib/supabase'
import { LoadingScreen } from '../../components/auth/ProtectedRoute'

export function AuthCallbackPage() {
  const { t } = useTranslation('auth')
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const handleCallback = async () => {
      // Get and clear any saved redirect destination (from invitation flow)
      const savedRedirect = localStorage.getItem('authRedirect')
      if (savedRedirect) {
        localStorage.removeItem('authRedirect')
      }
      const defaultRedirect = savedRedirect || '/dashboard'

      try {
        // Get the hash parameters (Supabase uses hash-based routing for auth)
        const hashParams = new URLSearchParams(window.location.hash.substring(1))
        const type = hashParams.get('type') || searchParams.get('type')

        // Check if we're on the reset-password path (Supabase redirect)
        const isRecoveryPath = window.location.pathname.includes('reset-password')

        // Check for error in hash
        const errorDescription = hashParams.get('error_description')
        if (errorDescription) {
          setError(errorDescription)
          return
        }

        // Check for code-based flow (PKCE) - used by Supabase for email links
        const code = searchParams.get('code')
        if (code) {
          const { data, error } = await supabase.auth.exchangeCodeForSession(code)
          if (error) {
            setError(error.message)
            return
          }

          // Check if this is a password recovery flow
          // The user object will have a recovery token type
          if (data.session?.user?.recovery_sent_at || type === 'recovery' || isRecoveryPath) {
            navigate('/reset-password', { replace: true })
          } else {
            navigate(defaultRedirect, { replace: true })
          }
          return
        }

        // Check for hash-based tokens (legacy or direct links)
        const accessToken = hashParams.get('access_token')
        const refreshToken = hashParams.get('refresh_token')

        if (accessToken) {
          // If we have both tokens, set session manually
          if (refreshToken) {
            const { error } = await supabase.auth.setSession({
              access_token: accessToken,
              refresh_token: refreshToken,
            })

            if (error) {
              setError(error.message)
              return
            }
          }

          // Handle recovery type - redirect to reset password
          if (type === 'recovery' || isRecoveryPath) {
            navigate('/reset-password', { replace: true })
            return
          }

          // For other types, go to saved redirect or dashboard
          navigate(defaultRedirect, { replace: true })
          return
        }

        // No auth parameters found - check if we already have a session
        // (Supabase might have handled the auth automatically)
        const { data: { session } } = await supabase.auth.getSession()
        if (session) {
          if (type === 'recovery' || isRecoveryPath) {
            navigate('/reset-password', { replace: true })
          } else {
            navigate(defaultRedirect, { replace: true })
          }
          return
        }

        // Nothing worked, go to login
        navigate('/login', { replace: true })
      } catch (err) {
        console.error('Auth callback error:', err)
        setError(t('callback.errorFallback'))
      }
    }

    handleCallback()
  }, [navigate, searchParams, t])

  if (error) {
    return (
      <div className="min-h-dvh bg-surface-page flex items-center justify-center px-4">
        <div className="w-full max-w-md text-center">
          <div className="w-16 h-16 bg-danger/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg
              className="w-8 h-8 text-danger"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>

          <h1 className="text-xl font-semibold text-ink mb-2">
            {t('callback.errorTitle')}
          </h1>
          <p className="text-ink-subtle mb-6">{error}</p>

          <button
            onClick={() => navigate('/login', { replace: true })}
            className="px-6 py-2 bg-accent text-accent-contrast font-medium rounded-lg
              hover:bg-accent-hover transition-colors"
          >
            {t('callback.returnToSignIn')}
          </button>
        </div>
      </div>
    )
  }

  return <LoadingScreen />
}
